import type { JSX } from 'react';
import { useState } from 'react';

import type { LoginErrorKind } from '@/lib/auth/loginError';
import { classifyLoginError } from '@/lib/auth/loginError';

import type { LoginValues } from './LoginForm';
import { LoginForm } from './LoginForm';

/**
 * The staff and admin sign-in: the form, its three states and the one-time code step.
 *
 * One component for both places it can appear — the customer app's `/login`, which is the
 * single sign-in for everyone who works in the salon, and the staff app's own `/login` when
 * it is served from a hostname of its own. The account decides what a person can open, so
 * there is no "staff" door and "admin" door; there is one form.
 *
 * What a successful sign-in *does* differs: the staff app fills its session store and
 * routes within itself, the customer app's `/login` leaves for `/staff/`. That is the
 * caller's `signIn` and `onSignedIn`; this owns only the state machine.
 */

/** What one submission sends. `totp` is `null` until the one-time code step is filled. */
export interface StaffCredentials {
  email: string;
  password: string;
  totp: string | null;
}

export function StaffSignIn({
  signIn,
  onSignedIn,
}: {
  /** Performs the sign-in; rejects with the api's problem on a refusal. */
  signIn: (credentials: StaffCredentials) => Promise<void>;
  onSignedIn: () => void;
}): JSX.Element {
  const [status, setStatus] = useState<'idle' | 'submitting'>('idle');
  const [errorKind, setErrorKind] = useState<LoginErrorKind | null>(null);
  const [showTotp, setShowTotp] = useState(false);

  function submit(values: LoginValues): void {
    setStatus('submitting');
    setErrorKind(null);
    void attempt(values);
  }

  async function attempt(values: LoginValues): Promise<void> {
    try {
      await signIn({
        email: values.email,
        password: values.password,
        totp: values.totp === '' ? null : values.totp,
      });
      onSignedIn();
    } catch (error) {
      const kind = classifyLoginError(error);
      setErrorKind(kind);
      // §5 answers one indistinguishable 401 for "wrong password" and for "this
      // account needs a one-time code", so the only honest move is to offer the
      // field and let the user decide which it was.
      if (kind === 'invalid') {
        setShowTotp(true);
      }
      setStatus('idle');
    }
  }

  return (
    <LoginForm status={status} errorKind={errorKind} showTotp={showTotp} onSubmit={submit} />
  );
}
